/**
 * Statuts Contrat V3, tirés de `docs/migration/status-mappings.md`. Les sept
 * statuts legacy possèdent tous une correspondance; `ARCHIVED` est dérivé du
 * soft delete et n'est donc jamais porté par la colonne `statut`.
 */
export type ContractStatus =
  'DRAFT' | 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'CANCELLED' | 'ARCHIVED'

// Sprint A (contract-first migration, `contracts.status_v2`) — modèle de
// statuts cible, pas encore câblé à l'UI ni à aucun repository (Sprint B/C).
export type ContractStatusV2 =
  | 'DRAFT'
  | 'PENDING_CLIENT'
  | 'VIEWED'
  | 'SIGNED'
  | 'AWAITING_PAYMENT'
  | 'ACTIVE'
  | 'DECLINED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'SUSPENDED'
  // Contrat d'essai terrain (12 oct 2026) : brouillon legacy, éligible aux Routes seulement.
  | 'TEST'

export type ContractDataSource = 'fixtures' | 'supabase' | 'fixture-fallback'

/**
 * Statut affiché, dérivé — jamais stocké.
 *
 * Le badge d'un Contrat ne peut pas se contenter de `status` : un Contrat
 * `ACTIVE` dont aucune signature n'a été constatée affichait « Actif » pendant
 * que son onglet Signature annonçait l'inverse. La dérivation suit le même
 * principe que `effectiveInvoiceStatus` côté Factures : la donnée stockée reste
 * intacte, seul l'affichage est recalculé à partir de toutes les colonnes qui
 * décrivent le même fait.
 */
export type ContractDisplayStatus =
  ContractStatus | 'AWAITING_SIGNATURE' | 'AWAITING_PAYMENT' | 'NOT_INTERESTED' | 'TEST'

export function deriveContractDisplayStatus(
  contract: Pick<ContractRecord, 'status' | 'signedOn' | 'sentAt' | 'statusV2'>,
): ContractDisplayStatus {
  // « Pas intéressé » (7 oct 2026) = refus du Client : `statut = 'annule'` +
  // `status_v2 = 'DECLINED'`. Il l'emporte sur « Annulé », qu'il rendrait ambigu.
  if (contract.statusV2 === 'DECLINED') return 'NOT_INTERESTED'
  // Contrat d'essai terrain : jamais confondu avec un vrai brouillon.
  if (contract.statusV2 === 'TEST') return 'TEST'
  // 18 sept 2026 : `SIGNED` (signature manuelle reçue par courriel, avant
  // paiement — `record_contract_manual_signature_v1`) affichait encore le
  // badge « Signature » du statut legacy `PENDING`, alors que le Contrat
  // attend déjà un paiement, pas une signature. Même case que
  // `AWAITING_PAYMENT`.
  if (contract.statusV2 === 'AWAITING_PAYMENT' || contract.statusV2 === 'SIGNED') return 'AWAITING_PAYMENT'
  // L'attente de signature ne se déduit que d'un envoi réel. Les Contrats
  // repris de l'ancien système n'ont jamais transité par l'application : les
  // marquer « en attente » signalerait une anomalie là où il n'y a qu'une
  // absence d'historique électronique.
  if (contract.status === 'ACTIVE' && !contract.signedOn && contract.sentAt) return 'AWAITING_SIGNATURE'
  return contract.status
}

/**
 * Étape du cycle de vie pour la section « Étapes du contrat » (Aperçu,
 * section 2026-09-17). Quatre paliers seulement — pas un sixième vocabulaire
 * de statut : `AWAITING_SIGNATURE`/`AWAITING_PAYMENT` regroupent chacun
 * plusieurs valeurs `statusV2` déjà distinguées ailleurs (Demandes,
 * `commercial_submission_stage_v1`).
 *
 * Deux chemins d'activation réels coexistent : le constat manuel de
 * signature (`record_contract_signature_v1`) active directement sans jamais
 * écrire `statusV2`, et le paiement en ligne
 * (`activate_contract_from_payment_v1`) passe par `AWAITING_PAYMENT`. Cette
 * fonction ne prétend donc jamais qu'une étape intermédiaire a eu lieu si
 * elle a été court-circuitée — elle ne fait que lire l'état courant.
 *
 * S'appuie sur `deriveContractDisplayStatus` pour l'anomalie legacy déjà
 * réglée là (`ACTIVE` sans signature constatée = en réalité en attente de
 * signature) et pour `AWAITING_PAYMENT` ; ajoute seulement `statusV2 ===
 * 'SIGNED'` (signé électroniquement, paiement pas encore reçu), absent du
 * badge d'affichage qui ne distingue pas ce cas de « en attente de
 * signature ».
 *
 * `CANCELLED`/`ARCHIVED` retombent sur `AWAITING_SIGNATURE` par défaut, sans
 * signification particulière : l'appelant ne doit pas rendre cette section
 * pour ces deux statuts (contrat abandonné, aucune action de cycle de vie
 * n'a de sens).
 */
export type ContractLifecycleStep = 'DRAFT' | 'AWAITING_SIGNATURE' | 'AWAITING_PAYMENT' | 'ACTIVE'

export function deriveContractLifecycleStep(
  contract: Pick<ContractRecord, 'status' | 'signedOn' | 'sentAt' | 'statusV2'>,
): ContractLifecycleStep {
  const displayStatus = deriveContractDisplayStatus(contract)
  if (displayStatus === 'ACTIVE' || displayStatus === 'SUSPENDED' || displayStatus === 'EXPIRED') {
    return 'ACTIVE'
  }
  if (displayStatus === 'AWAITING_PAYMENT' || contract.statusV2 === 'SIGNED') return 'AWAITING_PAYMENT'
  if (displayStatus === 'DRAFT' || displayStatus === 'TEST') return 'DRAFT'
  return 'AWAITING_SIGNATURE'
}

export type ContractMarkerState = 'REQUIRED' | 'IN_PLACE'

export interface ContractRouteAssignment {
  routeId: string
  name: string
  color?: string
}

export interface ContractRecord {
  id: string
  number: string
  status: ContractStatus
  clientId: string
  clientDisplayName: string
  clientNumber?: string
  clientPhone?: string
  clientMobilePhone?: string
  clientEmail?: string
  type?: string
  season?: string
  /**
   * Balises (colonne de la liste, 8 oct 2026) : `REQUIRED` = à installer, `IN_PLACE` = installées.
   * Absent = pas de balises (option décochée, contrat pas encore signé) ou information non chargée.
   */
  markerState?: ContractMarkerState
  /** Prix du contrat en cents, absent lorsque le legacy ne le porte pas. */
  priceCents?: number
  signedOn?: string
  startsOn?: string
  endsOn?: string
  renewal: boolean
  /** Adresse de service géocodée, section 13; à défaut celle du Client. */
  serviceLatitude?: number
  serviceLongitude?: number
  /** Somme des zones mesurées, en mètres carrés. */
  surfaceM2?: number
  /** Conditions enregistrées; chaque champ absent l'est réellement. */
  terms?: {
    services: string[]
    servedZone?: string
    exclusions?: string
    clientObligations?: string
    responsibilities?: string
    cancellationClause?: string
    priceClause?: string
    executionClause?: string
    insuranceClause?: string
    triggerThresholdCm?: number
    firstPassTime?: string
    finalCleanup?: string
    paymentMode?: string
    priceTaxes?: string
  }
  serviceAddress?: string
  /** Version de paramètres acceptée; absente pour les Contrats antérieurs. */
  settingsVersionId?: string
  notes?: string
  createdAt: string
  updatedAt: string
  /** Sprint A : projection non synchronisée avant Sprint B (voir `changeStatus`). */
  statusV2?: ContractStatusV2
  leadId?: string
  renewedFromContractId?: string
  measurementMode: 'NONE' | 'SATELLITE'
  standardDrivewayCapacity?: number
  sentAt?: string
  /** Dernier envoi (1er envoi ou relance) du courriel de signature. */
  lastSentAt?: string
  /** Dernière ouverture de la proposition par le client (lien public). */
  lastViewedAt?: string
  /**
   * Le client a ouvert la proposition depuis le dernier envoi/relance : remis à
   * `false` par chaque relance, repasse à `true` seulement à la réouverture.
   */
  openedSinceLastSend?: boolean
  submissionType?: 'RESIDENTIAL' | 'COMMERCIAL'
  billingAddress?: ContractPostalAddress
  /** Contrat « sans taxes » (bouton $$$) : jamais facturé ni encaissé. */
  sansFacturation?: boolean
  /** Masqué sur la carte de la vue d'ensemble (résidence hors de la zone de travail). Absent : affiché. */
  hiddenFromOverview?: boolean
  /**
   * Routes actives des adresses de service du Contrat (`route_contracts`), lues pour la liste
   * seulement. Absent si l'affectation n'est pas lisible; tableau vide : aucune route.
   */
  routeAssignments?: ContractRouteAssignment[]
}

export interface ContractPostalAddress {
  name?: string
  line1: string
  line2?: string
  city?: string
  province?: string
  postalCode?: string
  country?: string
}

/**
 * Corrige une adresse formatée dont le segment final (« ville, province,
 * code postal » ou « ville, province code postal » selon l'endroit qui l'a
 * construit) a été répété (bug historique : `line1` contenait déjà l'adresse
 * complète quand elle a été concaténée avec `city`/`province`/`postalCode`).
 * La taille du segment répété n'est pas fixe (2 ou 3 morceaux selon la
 * source), donc toutes les tailles de bloc sont essayées — seule une
 * répétition exacte et consécutive est corrigée; une adresse propre, ou une
 * rue qui contiendrait par coïncidence le nom de la ville une seule fois,
 * ressort inchangée.
 */
export function dedupeFormattedAddressTail(formatted: string): string {
  const parts = formatted
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
  if (parts.length < 4) return formatted
  const [street, city] = parts
  if (street === undefined || city === undefined) return formatted
  const tail = parts.slice(1)
  // Casse et espacement peuvent varier selon la source de chaque répétition
  // (ex. « Qc » depuis Mapbox vs « QC » stocké côté Client), et le nombre de
  // segments aussi (« ville, province postal » vs « ville, province,
  // postal ») : plutôt que de deviner la forme d'un bloc répété, on repère la
  // seconde apparition du nom de ville et on coupe tout ce qui suit — c'est
  // toujours là que commence la répétition.
  const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim()
  const cityNorm = normalize(city)
  const repeatIndex = tail.findIndex((segment, index) => index > 0 && normalize(segment) === cityNorm)
  if (repeatIndex === -1) return formatted
  return [street, ...tail.slice(0, repeatIndex)].join(', ')
}

/**
 * Rue seule, sans la ville/province/code postal.
 *
 * `line1` vient parfois d'une adresse Mapbox complète (« 222 Rue Scott,
 * Saint-Jérôme, Qc J7Z 1H1 ») stockée telle quelle, alors que les champs
 * `city`/`province`/`postalCode` existent aussi séparément. Affichée telle
 * quelle à côté de `locality()`, cette ligne répète la ville et le code
 * postal une seconde fois — cette fonction coupe `line1` à la première
 * apparition de la ville, ne gardant que la rue.
 */
export function streetLine(address: ContractPostalAddress): string {
  const { line1, city } = address
  if (!city) return line1
  const parts = line1
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
  if (parts.length < 2) return line1
  const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim()
  const cityNorm = normalize(city)
  const cityIndex = parts.findIndex((segment, index) => index > 0 && normalize(segment) === cityNorm)
  if (cityIndex === -1) return line1
  return parts.slice(0, cityIndex).join(', ')
}

export interface ClientProperty {
  id: string
  clientId: string
  address: ContractPostalAddress
  formattedAddress: string
  latitude?: number
  longitude?: number
}

export interface ContractServiceLocation {
  id: string
  contractId: string
  clientPropertyId?: string
  address: ContractPostalAddress
  formattedAddress: string
  latitude?: number
  longitude?: number
  services: string[]
  instructions?: string
  measurementMode: 'NONE' | 'SATELLITE'
  standardDrivewayCapacity?: number
  priceCents?: number
  order: number
  /** Absent : en service. Suspendue : garde sa route, sautée par les opérations. Retirée : hors du Contrat. */
  serviceStatus?: 'SUSPENDED' | 'REMOVED'
}

/** État d'une adresse de service (24 oct. 2026) : seule la fiche Contrat le change. */
export type ServiceLocationStatus = 'ACTIVE' | 'SUSPENDED' | 'REMOVED'

export interface ConfigureContractLocationsCommand {
  idempotencyKey: string
  contractId: string
  submissionType: 'RESIDENTIAL' | 'COMMERCIAL'
  billingAddress: ContractPostalAddress
  locations: {
    clientPropertyId?: string
    address: ContractPostalAddress
    formattedAddress?: string
    latitude?: number
    longitude?: number
    services?: string[]
    instructions?: string
    measurementMode: 'NONE' | 'SATELLITE'
    standardDrivewayCapacity?: number
    priceCents?: number
  }[]
}

export interface ContractStats {
  total: number
  draft: number
  pending: number
  active: number
  suspended: number
  expired: number
  cancelled: number
  /** Statut affiché « en attente de signature », proposition pas (re)ouverte depuis le dernier envoi. */
  awaitingSignature: number
  /** Statut affiché « en attente de signature », proposition ouverte depuis le dernier envoi. */
  awaitingSignatureOpened: number
  /** Statut affiché « en attente de paiement » (dérivé). */
  awaitingPayment: number
  /** Contrats dont le statut affiché est exactement « Actif » (signés, sans attente). */
  activeSigned: number
  /** Contrats refusés par le Client (statut affiché « Pas intéressé »). */
  notInterested: number
  /** Contrats VIP (bouton $$$, `sansFacturation`) actifs — sous-ensemble de `activeSigned`. */
  vip: number
}

export interface ContractQueryMeta {
  source: ContractDataSource
  retrievedAt: string
  fallbackReason?: string
  anomalies: number
}

export interface ContractPage {
  items: ContractRecord[]
  page: number
  pageSize: number
  total: number
  stats: ContractStats
  meta: ContractQueryMeta
}

export interface ContractDetailResult {
  contract: ContractRecord
  meta: ContractQueryMeta
}

export interface CreateContractFromQuoteCommand {
  idempotencyKey: string
  quoteId: string
  /** Exigée par la section 55 : le Contrat la demande, la Soumission ne la porte pas. */
  season: string
  type?: string
}

export interface CreateContractFromQuoteResult {
  contractId: string
  number: string
  status: 'DRAFT'
  clientId: string
  season: string
  priceCents: number
  quoteId: string
  quoteNumber: string | null
}

export interface ContractListFilters {
  page: number
  pageSize: number
  search?: string
  status?: ContractStatus
  /**
   * Filtre par statut *affiché* (dérivé) — utilisé par les cartes en tête de la
   * liste des Contrats : « en attente de signature », « en attente de paiement »
   * et « actif » ne sont pas des valeurs de `contracts.statut`. Exclusif avec
   * `status`.
   */
  displayStatus?:
    /** « À signer » : en attente de signature, ouverte ou non par le client (les deux vues réunies). */
    | 'AWAITING_SIGNATURE_ANY'
    | 'AWAITING_SIGNATURE'
    | 'AWAITING_SIGNATURE_OPENED'
    | 'AWAITING_PAYMENT'
    | 'ACTIVE'
    | 'DRAFT'
    | 'SUSPENDED'
    | 'EXPIRED'
    | 'CANCELLED'
    | 'NOT_INTERESTED'
  /** Sprint D : Contrats d'un Client donné, pour le point d'entrée renouvellement. */
  clientId?: string
  /**
   * Parcours du Contrat (`contracts.contract_flow`). `LEGACY` : liste Résidentiel, sans les
   * Contrats vNext (Commercial et Multilogements), qui ont leurs propres listes.
   */
  flow?: 'LEGACY'
  /**
   * Contrats VIP : créés avec le bouton $$$ (`sansFacturation`) *et* actifs. Un
   * VIP n'est jamais en attente de signature ni de paiement. Exclusif avec
   * `displayStatus`.
   */
  vip?: boolean
  /** Tri serveur — la liste est paginée, un tri local ne trierait qu'une page. */
  sort?: ContractListSort
}

export type ContractSortKey = 'number' | 'client' | 'status' | 'price'

export interface ContractListSort {
  key: ContractSortKey
  direction: 'asc' | 'desc'
}

/** Même règle que le filtre VIP du repository : sans facturation ET actif. */
export function isVipContract(
  contract: Pick<ContractRecord, 'sansFacturation' | 'status' | 'signedOn' | 'sentAt' | 'statusV2'>,
): boolean {
  return contract.sansFacturation === true && deriveContractDisplayStatus(contract) === 'ACTIVE'
}

/**
 * Un Contrat correspond-il à un filtre de statut affiché ? Même dérivation que
 * le badge (`deriveContractDisplayStatus`), pour que carte, filtre et badge ne
 * puissent jamais se contredire.
 */
export function contractMatchesDisplayStatus(
  contract: Pick<ContractRecord, 'status' | 'signedOn' | 'sentAt' | 'statusV2' | 'openedSinceLastSend'>,
  displayStatus: NonNullable<ContractListFilters['displayStatus']>,
): boolean {
  // `a_signer`/`en_attente` (PENDING) comptent « en attente de signature » même sans
  // envoi enregistré : c'est la règle du repository SQL, la partition doit être la même.
  const raw = deriveContractDisplayStatus(contract)
  const derived = raw === 'PENDING' ? 'AWAITING_SIGNATURE' : raw
  if (displayStatus === 'ACTIVE') return derived === 'ACTIVE'
  if (displayStatus === 'AWAITING_SIGNATURE_ANY') return derived === 'AWAITING_SIGNATURE'
  if (displayStatus === 'AWAITING_SIGNATURE') {
    return derived === 'AWAITING_SIGNATURE' && !contract.openedSinceLastSend
  }
  if (displayStatus === 'AWAITING_SIGNATURE_OPENED') {
    return derived === 'AWAITING_SIGNATURE' && contract.openedSinceLastSend === true
  }
  return derived === displayStatus
}

/** Vue « Contrats supprimés » — liste des Contrats en soft delete (`deleted_at` non nul). */
export interface ContractDeletedListFilters {
  page: number
  pageSize: number
  search?: string
  /** Parcours (`contracts.contract_flow`) : voir `ContractListFilters.flow`. */
  flow?: 'LEGACY'
}

export interface ContractDeletedPage {
  items: ContractRecord[]
  page: number
  pageSize: number
  total: number
  meta: ContractQueryMeta
}

/**
 * Ligne de la vue `pending_contracts` (Sprint A, colonnes coordonnées Sprint
 * C) : module Soumissions désormais adossé aux Contrats en attente du
 * Client, section 20-24 de la tâche Sprint C.
 */
export interface PendingContractRecord {
  contractId: string
  contractNumber: string
  clientId: string
  clientName: string
  serviceAddress?: string
  clientPhone?: string
  clientEmail?: string
  statusV2: 'PENDING_CLIENT' | 'VIEWED'
  totalAmountCents: number
  sentAt?: string
  lastFollowUpAt?: string
  nextFollowUpAt?: string
  followUpOwnerId?: string
  followUpOwnerEmail?: string
}

export interface PendingContractPage {
  items: PendingContractRecord[]
  meta: ContractQueryMeta
}

export type PendingContractQuickFilter = 'ALL' | 'NOT_VIEWED' | 'VIEWED' | 'FOLLOW_UP_TODAY' | 'OVERDUE'

export const pendingContractStatusLabel: Record<PendingContractRecord['statusV2'], string> = {
  PENDING_CLIENT: 'En attente du client',
  VIEWED: 'Consulté',
}

export interface ScheduleContractFollowUpCommand {
  contractId: string
  nextFollowUpAt: string
  followUpOwnerId?: string
}

export interface ScheduleContractFollowUpResult {
  contractId: string
  nextFollowUpAt: string
  followUpOwnerId?: string
}

/**
 * Bornes de la période de service d'un Contrat.
 *
 * Les dates explicites (`date_debut` / `date_fin`) priment toujours. À défaut,
 * la saison « AAAA-BBBB » désigne, pour tous les Contrats de RÉCA, la période
 * du 1er novembre AAAA au 1er avril BBBB — même règle métier que le backfill
 * `20260910010000_contract_service_period_backfill_v1`. Un Contrat sans dates
 * ni saison canonique n'a pas de période déductible.
 */
export function contractServicePeriod(contract: {
  startsOn?: string | undefined
  endsOn?: string | undefined
  season?: string | undefined
}): { startsOn: string; endsOn: string } | undefined {
  if (contract.startsOn && contract.endsOn) {
    return { startsOn: contract.startsOn, endsOn: contract.endsOn }
  }
  const season = contract.season?.trim() ?? ''
  if (!/^\d{4}-\d{4}$/.test(season)) return undefined
  return { startsOn: `${season.slice(0, 4)}-11-01`, endsOn: `${season.slice(5, 9)}-04-01` }
}

/**
 * Un Contrat actif sans période déductible (ni dates, ni saison canonique) ne
 * permet pas de planifier la saison. L'anomalie est signalée, jamais corrigée.
 */
export function hasContractPeriod(contract: ContractRecord): boolean {
  return contractServicePeriod(contract) !== undefined
}

/**
 * Étapes du wizard officiel, section 9 du document 08 — libellés alignés sur
 * l'audit UX/UI Phase 4 (16 sept 2026, §5.4) le 16 sept 2026 : « Analyse et
 * zones » et « Services et obligations » fusionnent sous une seule étape
 * (deux composants distincts, un seul `StepFooter`); « Conditions du
 * contrat » est une étape neuve entre Prix et Vérification.
 */
export const contractWizardSteps = [
  'Client et adresse',
  'Propriété et services',
  'Prix et paiement',
  'Conditions du contrat',
  'Vérification et envoi',
] as const

export interface CreateContractDraftCommand {
  idempotencyKey: string
  clientId: string
  season: string
  type?: string
  serviceAddress?: string
  latitude?: number
  longitude?: number
  leadId?: string
  measurementMode?: 'NONE' | 'SATELLITE'
  standardDrivewayCapacity?: number
  renewedFromContractId?: string
}

export interface CreateContractDraftResult {
  contractId: string
  number: string
  status: 'DRAFT'
  clientId: string
  season: string
  measurementMode?: 'NONE' | 'SATELLITE'
}

/**
 * Conditions d'un Contrat. Chaque champ est facultatif : le wizard enregistre
 * étape par étape, et un champ absent laisse la valeur existante intacte.
 */
export interface ContractTermsCommand {
  contractId: string
  services?: string[]
  paymentTerms?: { label: string; percentage?: number }[]
  paymentMode?: string
  triggerThresholdCm?: string
  maximumAccumulationCm?: string
  safetyDistanceCm?: string
  firstPassTime?: string
  finalCleanup?: string
  /** Registre fermé du schéma legacy. */
  snowDeposit?: 'sur_terrain' | 'bordure_rue' | 'transport_hors_site' | ''
  conclusionMode?: 'en_personne' | 'a_distance' | 'itinerant' | ''
  priceTaxes?: 'avant_taxes' | 'apres_taxes' | 'sans_taxes' | ''
  markersRequired?: boolean
  municipalPermitObtained?: boolean
  exclusions?: string
  clientObligations?: string
  responsibilities?: string
  cancellationClause?: string
  priceClause?: string
  executionClause?: string
  insuranceClause?: string
  knownObstacles?: string
  operatorMessage?: string
  specialInstructions?: string
  servedZone?: string
  measurementMode?: 'NONE' | 'SATELLITE'
  standardDrivewayCapacity?: number
  /** Cents. Absence de grille tarifaire dans ce dépôt : saisie manuelle. */
  priceCents?: number
  /** Requis par le serveur pour changer un prix déjà enregistré (`contract.price_override`). */
  priceOverride?: boolean
}

/** Services proposés, tels que la landing les présente aux visiteurs. */
export const contractServiceOptions = [
  { key: 'balises_requises', label: 'Balises requises' },
  { key: 'deneigement_complet', label: 'Déneigement du stationnement' },
  { key: 'sable_sel', label: 'Sable et sel' },
  { key: 'bordages', label: 'Bordages' },
  { key: 'trottoirs', label: 'Trottoirs et escaliers' },
  { key: 'transport_neige', label: 'Transport de la neige' },
] as const

/** Cochés par défaut quand une adresse de service n'a encore aucun service choisi. */
export const defaultSelectedContractServiceKeys: string[] = ['balises_requises', 'deneigement_complet']

export const conclusionModeOptions = [
  { key: 'en_personne', label: 'En personne' },
  { key: 'a_distance', label: 'À distance' },
  { key: 'itinerant', label: 'Itinérant' },
] as const

/** Types d'entrée d'échéancier, section 84. */
export const scheduleEntryKinds = ['PERCENTAGE', 'AMOUNT'] as const
export type ScheduleEntryKind = (typeof scheduleEntryKinds)[number]

export interface ContractScheduleEntry {
  id: string
  description: string
  kind: ScheduleEntryKind
  value: number
  amountCents: number
  dueOn?: string
  order: number
}

export interface ReplaceContractScheduleCommand {
  idempotencyKey: string
  contractId: string
  entries: { description: string; kind: ScheduleEntryKind; value: number; dueOn?: string }[]
}

export interface ReplaceContractScheduleResult {
  contractId: string
  entries: number
  scheduledCents: number
  totalCents: number
}

/** Méthodes de signature constatées, section 97; aucune n'est produite ici. */
export const signatureMethods = [
  { key: 'EN_PERSONNE', label: 'En personne' },
  { key: 'COURRIEL', label: 'Par courriel' },
  { key: 'TELEPHONE', label: 'Par téléphone' },
  { key: 'AUTRE', label: 'Autre' },
] as const

export type RecordSignatureMethod = (typeof signatureMethods)[number]['key']
export type SignatureMethod = RecordSignatureMethod | 'PAYMENT_PAGE' | 'CLIENT_PORTAL'

/** Ligne du journal d'un Contrat (`contract_events`) — jamais une donnée fabriquée. */
export interface ContractEvent {
  id: string
  type: string
  createdAt: string
  /** Utilisateur à l'origine de l'action; absent pour les actions du client (lien public) ou du système. */
  authorName?: string
  payload?: Record<string, unknown>
}

/**
 * Note interne d'un Contrat (`contract_notes`, fil partagé avec l'application
 * historique). Visible de l'équipe seulement : jamais reprise dans le PDF ni
 * dans un courriel. Modifiable par son auteur ou un administrateur, comme le
 * prévoit la RLS de la table.
 */
export interface ContractNote {
  id: string
  contractId: string
  message: string
  createdAt: string
  updatedAt: string
  /** `public.users.id` de l'auteur; absent pour une note historique sans auteur. */
  authorId?: string
  authorName?: string
}

export const contractNoteMaxLength = 4000

/** Plus récente d'abord, comme l'historique du Contrat. */
export function sortContractNotes(notes: readonly ContractNote[]): ContractNote[] {
  return [...notes].sort((left, right) => right.createdAt.localeCompare(left.createdAt))
}

/** Miroir de la RLS `contract_notes_update_author_or_admin`, pour n'offrir que ce que le serveur acceptera. */
export function canEditContractNote(
  note: Pick<ContractNote, 'authorId'>,
  session: { userId: string; authUserId: string; roles: readonly string[] },
): boolean {
  if (session.roles.includes('ADMINISTRATOR')) return true
  return (
    note.authorId !== undefined && (note.authorId === session.userId || note.authorId === session.authUserId)
  )
}

export interface ContractSignature {
  id: string
  signedAt: string
  signerName: string
  signerRole?: string
  method: SignatureMethod
  proof?: string
}

export interface RecordContractSignatureCommand {
  contractId: string
  signedAt: string
  signerName: string
  signerRole?: string
  method: RecordSignatureMethod
  proof?: string
}

export interface RecordContractSignatureResult {
  contractId: string
  number: string
  status: 'ACTIVE'
  signatureId: string
  signedAt: string
  /** Zéro signale une activation sans mesure : à afficher, jamais à bloquer. */
  measuredZones: number
}

/**
 * Contrat reçu déjà signé par un canal hors application (courriel, téléphone,
 * de vive voix) — 18 sept 2026, méthode ajoutée le 20 sept 2026. Action
 * distincte de `RecordContractSignatureCommand` : celle-ci n'active jamais
 * directement (`status_v2` devient `SIGNED`, jamais `statut = 'actif'`), le
 * paiement reste une étape distincte. Le fichier joint (facultatif) passe par
 * `archiveDocument`, pas par cette commande. `method` est constatée par
 * l'utilisateur, jamais déduite.
 */
export type ContractManualSignatureMethod = 'COURRIEL' | 'TELEPHONE' | 'EN_PERSONNE'

export interface RecordContractManualSignatureCommand {
  contractId: string
  signedAt: string
  signerName: string
  method: ContractManualSignatureMethod
  referenceNumber?: string
}

export interface RecordContractManualSignatureResult {
  contractId: string
  number: string
  status: 'SIGNED'
  signatureId: string
  signedAt: string
  method: ContractManualSignatureMethod
}

/**
 * Contrat « sans taxes » (bouton $$$, 18 sept 2026) : gardé pour les
 * informations opérationnelles (adresse, services) utiles cet hiver, mais
 * jamais facturé ni encaissé — distinct de `priceTaxes: 'sans_taxes'`
 * (option normale du formulaire de création, toujours facturée, seulement
 * sans TPS/TVQ ajoutée). Active directement, sans signature ni paiement.
 */
export interface ActivateContractNoInvoiceCommand {
  contractId: string
  amountCents: number
}

export interface ActivateContractNoInvoiceResult {
  contractId: string
  number: string
  status: 'ACTIVE'
  priceCents: number
}

export interface ChangeContractStatusCommand {
  contractId: string
  /** Statut legacy visé; les transitions permises sont celles de la section 94. */
  status: 'a_signer' | 'actif' | 'suspendu' | 'annule'
  reason?: string
}

/**
 * Somme des versements en cents. Sert à montrer l'écart pendant la saisie :
 * le serveur refuse un échéancier qui ne fait pas le total.
 */
export function scheduledCents(
  entries: { kind: ScheduleEntryKind; value: number }[],
  totalCents: number,
): number {
  return entries.reduce(
    (sum, entry) =>
      sum +
      (entry.kind === 'PERCENTAGE'
        ? Math.round((totalCents * entry.value) / 100)
        : Math.round(entry.value * 100)),
    0,
  )
}

/**
 * Montant de chaque versement en cents, pour l'aperçu pendant la saisie.
 *
 * Quand toutes les parts sont en pourcentage et font exactement 100 %, le
 * dernier versement reçoit le reste : sinon 50/50 sur un total impair dépasse
 * d'un cent et l'écart affiché ne tombe jamais à zéro. Le serveur garde ses
 * propres arrondis (tolérance d'un cent par versement).
 */
export function scheduleEntryCents(
  entries: { kind: ScheduleEntryKind; value: number }[],
  totalCents: number,
): number[] {
  const amounts = entries.map((entry) => scheduledCents([entry], totalCents))
  const allPercentages = entries.every((entry) => entry.kind === 'PERCENTAGE')
  const percentSum = entries.reduce((sum, entry) => sum + entry.value, 0)
  if (entries.length > 1 && allPercentages && Math.abs(percentSum - 100) < 1e-9) {
    const head = amounts.slice(0, -1).reduce((sum, cents) => sum + cents, 0)
    amounts[amounts.length - 1] = totalCents - head
  }
  return amounts
}

/**
 * Résultat d'un envoi du document contractuel au Client.
 *
 * L'envoi ne change aucun statut : il archive une version du document et la
 * transmet. `replayed` distingue une reprise idempotente d'un envoi réel, pour
 * que l'interface n'annonce pas deux fois le même courriel.
 */
export interface SendContractDocumentResult {
  contractId: string
  number: string
  documentVersion?: number
  replayed: boolean
}

/** Taux du profil fiscal actif, rendu par `active_tax_rates_ppm_v1()`. */
export interface ContractTaxRatePpm {
  code: string
  ratePpm: number
}

export interface ContractPriceBreakdown {
  subtotalCents: number
  gstCents: number
  qstCents: number
  totalCents: number
  gstPpm: number
  qstPpm: number
  /** Codes fiscaux hors TPS/TVQ : signalés, jamais fondus dans un total. */
  unmappedTaxes: string[]
}

/**
 * Détail Sous-total / TPS / TVQ / TOTAL d'un Contrat.
 *
 * Les taux viennent du profil fiscal actif, lu côté serveur par
 * `active_tax_rates_ppm_v1()` : aucune valeur fiscale n'est écrite ici. Un
 * Contrat dont le prix est annoncé « après taxes » a déjà tout inclus; le
 * sous-total en est déduit plutôt que recalculé au-dessus.
 */
export function contractPriceBreakdown(
  priceCents: number,
  priceTaxes: string | undefined,
  rates: ContractTaxRatePpm[],
): ContractPriceBreakdown {
  const gstPpm = rates.find((rate) => rate.code === 'TPS' || rate.code === 'GST')?.ratePpm ?? 0
  const qstPpm = rates.find((rate) => rate.code === 'TVQ' || rate.code === 'QST')?.ratePpm ?? 0
  const unmappedTaxes = rates
    .filter((rate) => !['TPS', 'GST', 'TVQ', 'QST'].includes(rate.code))
    .map((rate) => rate.code)
  if (priceTaxes === 'sans_taxes') {
    return {
      subtotalCents: priceCents,
      gstCents: 0,
      qstCents: 0,
      totalCents: priceCents,
      gstPpm: 0,
      qstPpm: 0,
      unmappedTaxes,
    }
  }
  const subtotalCents =
    priceTaxes === 'apres_taxes'
      ? Math.round((priceCents * 1_000_000) / (1_000_000 + gstPpm + qstPpm))
      : priceCents
  const gstCents = Math.round((subtotalCents * gstPpm) / 1_000_000)
  const qstCents = Math.round((subtotalCents * qstPpm) / 1_000_000)
  return {
    subtotalCents,
    gstCents,
    qstCents,
    totalCents: subtotalCents + gstCents + qstCents,
    gstPpm,
    qstPpm,
    unmappedTaxes,
  }
}

export interface GenerateContractInvoicesResult {
  contractId: string
  invoices: number
  numbers: string[]
  totalWithTaxes: number
  gstPpm: number
  qstPpm: number
  /** Codes fiscaux que le schéma legacy ne sait pas ranger; à signaler. */
  unmappedTaxes: string[]
}

/** Résultat de « Pas intéressé » sur une ou plusieurs lignes : le refus d'une ligne ne bloque pas les autres. */
export interface MarkNotInterestedResult {
  updated: { contractId: string; invoicesCancelled: number }[]
  skipped: { contractId: string; reason: 'NOT_FOUND' | 'ALREADY_CLOSED' | 'HAS_PAYMENTS' }[]
}
